import React, { useState } from "react";
import styled from "styled-components";
import PropTypes from "prop-types";
import { getColorHexFromId } from "lib/utils";
import Image from "next/image";
import ActionItem from "components/ActionItem";
import useOutsideClickHider from "components/helpers/OutsideClickHider";
import { DropDownList, DropDownListItem } from "components/Shared/dropdown";
import { ProfileHeadText, ImgContainer, ProfileIcon } from "components/Shared";

const ProfileDropDown = ({ hideList, profileItems, providerId }) => {
  return (
    <DropDownList hideList={hideList}>
      <div>
        {profileItems.map((item) => {
          return (
            <DropDownListItem key={item.id}>
              <ActionItem key={item.id} {...item} providerId={providerId} />
            </DropDownListItem>
          );
        })}
      </div>
    </DropDownList>
  );
};

ProfileDropDown.propTypes = {
  hideList: PropTypes.bool,
  profileItems: PropTypes.arrayOf(PropTypes.object),
  providerId: PropTypes.string,
};

ProfileDropDown.defaultProps = {
  hideList: true,
  profileItems: true,
  providerId: "",
};

const ProfileIconInitials = styled.p`
  font-size: 1.125rem;
  height: 1.125rem;
  line-height: 1;
  margin: 0.25rem 0 0 0;
`;

const ProfileWrapper = styled.div`
  font-weight: 600;
  margin: auto 0;
  display: ${({ hideLgSidebarProvider }) =>
    hideLgSidebarProvider ? "block" : "none"};

  /* Update hover state for desktop nav ProfileWrapper */
  ${({ theme }) => theme.mq.lg`
    display: block;

    :hover {
      & ${ProfileIcon} {
        border: 0.125rem solid ${theme.themeColors.primary};
      }
    }
  `}
`;

const IconHeadWrapper = styled.div`
  display: inline-flex;
  margin: 0.625rem 1.5625rem;

  ${({ isClickable }) => (isClickable ? "cursor: pointer;" : null)}
  ${({ theme }) => theme.mq.lg`
    margin: 0.625rem 0;
  `}
`;

const HeadTextWrapper = styled.div`
  margin: auto 0.625rem;
  color: #27272f;
`;

const ProfileHeadSubText = styled.div`
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  width: auto;
  max-width: 19.375rem;
  font-size: ${({ theme }) => theme.fontSize(2)};
  line-height: 125.3%;
`;

const ProfileCardListWrapper = styled.div`
  width: 100%;
  border-radius: 0.75rem;
  color: #27272f;
  justify-content: space-between;
`;

const ProfileCardWrapper = styled.div`
  padding: 0 1.5625rem;
`;

const ProfileOuter20 = styled.div`
  margin: 0 0 1.25rem 0;

  ${({ theme }) => theme.mq.lg`
    display: none;
  `}

  /* Safari iOS Portrait and Landscape */
  @media only screen
    and (min-device-width: 320px)
    and (max-device-width: 480px)
    and (-webkit-min-device-pixel-ratio: 2) {
    padding-bottom: 2rem;
  }
`;

const ProfileActionItemWrapper = styled.div`
  display: inline-block;
  width: 100%;
  margin: 0.9375rem 0;
`;

const StyledImage = styled(Image)``;

const ProfileCardSideBarList = ({ profileItems }) => {
  return (
    <ProfileOuter20>
      <ProfileCardWrapper>
        <ProfileCardListWrapper>
          <div>
            {profileItems.map((item) => {
              return (
                <ProfileActionItemWrapper key={item.id}>
                  <ActionItem {...item} />
                </ProfileActionItemWrapper>
              );
            })}
          </div>
        </ProfileCardListWrapper>
      </ProfileCardWrapper>
    </ProfileOuter20>
  );
};

ProfileCardSideBarList.propTypes = {
  profileItems: PropTypes.arrayOf(PropTypes.object),
  profileCardIdentity: PropTypes.shape({
    title: PropTypes.string,
    name: PropTypes.string,
  }),
};

ProfileCardSideBarList.defaultProps = {
  profileItems: [],
  profileCardIdentity: { title: "", name: "" },
};

const ProfileCard = ({
  setHideList,
  hideList,
  profileCardIdentity,
  isCustomerNav,
  isProviderNav,
  isAdminNav,
  isClickable,
}) => {
  const firstName =
    profileCardIdentity?.firstName || profileCardIdentity?.first_name;
  const lastName =
    profileCardIdentity?.lastName || profileCardIdentity?.last_name;
  const fullName =
    profileCardIdentity && firstName && lastName
      ? `${firstName} ${lastName}`
      : null;
  return (
    <IconHeadWrapper
      isClickable={isClickable}
      onClick={() => {
        setHideList(!hideList);
      }}
    >
      {profileCardIdentity?.image?.sizes?.small?.url ? (
        <ImgContainer>
          <StyledImage
            alt="profile image"
            objectFit="cover"
            objectPosition="center"
            height={46}
            width={46}
            src={profileCardIdentity.image.sizes.small.url}
          />
        </ImgContainer>
      ) : (
        <ProfileIcon color={getColorHexFromId(profileCardIdentity?.id)}>
          <ProfileIconInitials>
            {firstName?.charAt(0)?.toUpperCase() || ""}
            {lastName?.charAt(0)?.toUpperCase() || ""}
          </ProfileIconInitials>
        </ProfileIcon>
      )}
      <HeadTextWrapper>
        <ProfileHeadText>
          {isCustomerNav && "Pet's Parent"}
          {isAdminNav && "Admin User"}
          {isProviderNav &&
            (profileCardIdentity?.business?.businessName || "No Business Yet")}
        </ProfileHeadText>
        <ProfileHeadSubText>{fullName || "Anonymous"}</ProfileHeadSubText>
      </HeadTextWrapper>
    </IconHeadWrapper>
  );
};

ProfileCard.propTypes = {
  setHideList: PropTypes.func,
  hideList: PropTypes.bool,
  profileCardIdentity: PropTypes.shape({
    title: PropTypes.string,
    name: PropTypes.string,
  }),
};

ProfileCard.defaultProps = {
  setHideList: {},
  hideList: true,
  profileCardIdentity: { title: "", name: "" },
};

export default function Profile({
  hideLgSidebarProvider,
  profileItems,
  profileCardIdentity,
  isCustomerNav,
  isAdminNav,
  isProviderNav,
  providerId,
}) {
  const [hideList, setHideList] = useState(true);
  const ListEl = React.useRef(null);
  useOutsideClickHider(ListEl, setHideList);
  return (
    <ProfileWrapper hideLgSidebarProvider={hideLgSidebarProvider} ref={ListEl}>
      <ProfileCard
        isClickable={!hideLgSidebarProvider}
        profileCardIdentity={profileCardIdentity}
        setHideList={setHideList}
        hideList={hideList}
        isCustomerNav={isCustomerNav}
        isProviderNav={isProviderNav}
        isAdminNav={isAdminNav}
      />
      <ProfileCardSideBarList profileItems={profileItems} />
      <ProfileDropDown
        profileItems={profileItems}
        hideList={hideList}
        providerId={providerId}
      />
    </ProfileWrapper>
  );
}

ProfileCard.propTypes = {
  isCustomerNav: PropTypes.bool,
  isAdminNav: PropTypes.bool,
  isProviderNav: PropTypes.bool,
  profileCardIdentity: PropTypes.shape({
    id: PropTypes.string,
    image: PropTypes.shape({ sizes: { small: { url: PropTypes.string } } }),
    title: PropTypes.string,
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    first_name: PropTypes.string,
    last_name: PropTypes.string,
    business: PropTypes.shape({ businessName: PropTypes.string }),
  }),
  isClickable: PropTypes.bool,
};

ProfileCard.defaultProps = {
  isCustomerNav: false,
  isProviderNav: false,
  isAdminNav: false,
  profileCardIdentity: {},
  isClickable: true,
};

Profile.propTypes = {
  isAdminNav: PropTypes.bool,
  isProviderNav: PropTypes.bool,
  isCustomerNav: PropTypes.bool,
  hideLgSidebarProvider: PropTypes.bool,
  providerId: PropTypes.string,
  profileItems: PropTypes.arrayOf(PropTypes.object),
  profileCardIdentity: PropTypes.shape({
    title: PropTypes.string,
    firstName: PropTypes.string,
    lastName: PropTypes.string,
  }),
};

Profile.defaultProps = {
  isAdminNav: false,
  isProviderNav: false,
  isCustomerNav: false,
  hideLgSidebarProvider: false,
  providerId: "",
  profileItems: [],
  profileCardIdentity: {},
};
